import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import Header from "@/components/Header";
import ReportModal from "@/components/ReportModal";
import AssessmentModal from "@/components/AssessmentModal";
import RescheduleModal from "@/components/RescheduleModal";
import { StatusBadge, TimelinessBadge, VisitTypeBadge, QualitySummary, RescheduleNote } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  CalendarDays, Clock, MapPin, Check, X, Sparkles, Loader2, FileText,
  Users, ClipboardCheck, ClipboardList, TimerReset, TrendingUp, UserPlus, UserCheck,
  UserX, CalendarClock, Lock,
} from "lucide-react";

function Stat({ icon: Icon, label, value, tone, bg }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${bg}`}>
          <Icon className={`h-5 w-5 ${tone}`} />
        </div>
        <div>
          <p className="font-display text-2xl font-bold leading-none text-slate-900">{value}</p>
          <p className="mt-1 text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

export default function SocialWorkerDashboard() {
  const [visits, setVisits] = useState([]);
  const [stats, setStats] = useState({});
  const [parents, setParents] = useState([]);
  const [reports, setReports] = useState([]);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewNote, setReviewNote] = useState("");
  const [genFor, setGenFor] = useState(null);
  const [activeReport, setActiveReport] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [assessTarget, setAssessTarget] = useState(null);
  const [assessOpen, setAssessOpen] = useState(false);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [noShowTarget, setNoShowTarget] = useState(null);
  const [noShowReason, setNoShowReason] = useState("");
  const [me, setMe] = useState(null);

  const load = async () => {
    try {
      const [v, s, p, r, m] = await Promise.all([
        api.get("/visits"), api.get("/stats"), api.get("/parents"), api.get("/reports"), api.get("/auth/me"),
      ]);
      setVisits(v.data);
      setStats(s.data);
      setParents(p.data);
      setReports(r.data);
      setMe(m.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openAssess = (v) => {
    setAssessTarget(v);
    setAssessOpen(true);
  };

  const markNoShow = async (id, reason = "") => {
    try {
      await api.put(`/visits/${id}/no-show`, { reason });
      toast.success("Visit marked as No Show");
      setNoShowTarget(null);
      setNoShowReason("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const review = async (id, status, note = "") => {
    try {
      await api.put(`/visits/${id}/review`, { status, review_note: note });
      toast.success(status === "approved" ? "Visit approved" : "Visit marked not approved");
      setReviewTarget(null);
      setReviewNote("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const reviewSeries = async (seriesId, status) => {
    try {
      const { data } = await api.put(`/visits/series/review`, { series_id: seriesId, status, review_note: "" });
      toast.success(`${data.updated} visits ${status === "approved" ? "approved" : "declined"}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const generate = async (parentId) => {
    setGenFor(parentId);
    try {
      const { data } = await api.post("/reports/generate", { parent_id: parentId });
      toast.success("AI report generated");
      setActiveReport(data);
      setReportOpen(true);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setGenFor(null);
    }
  };

  const pending = visits.filter((v) => v.status === "pending");
  const pendingSeries = Object.values(
    pending.reduce((acc, v) => {
      if (!v.series_id) return acc;
      if (!acc[v.series_id]) acc[v.series_id] = { series_id: v.series_id, parent_name: v.parent_name, child_name: v.child_name, recurrence: v.recurrence, count: 0 };
      acc[v.series_id].count += 1;
      return acc;
    }, {})
  ).filter((s) => s.count > 1);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header subtitle="Social Worker Command Center" />
      {me && me.access_status && me.access_status !== "active" ? (
        <main className="mx-auto max-w-2xl px-4 py-24 text-center" data-testid="pending-access-screen">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-10">
            <Lock className="mx-auto h-12 w-12 text-amber-500" />
            <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Access Pending Approval</h1>
            <p className="mt-3 text-slate-600">
              Your social worker account is <span className="font-semibold capitalize">{me.access_status}</span>.
              You do not yet have access to client, visit, or report data. Please contact the administrator to
              be assigned to a county and activated.
            </p>
          </div>
        </main>
      ) : (
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="animate-fade-up">
          <h1 className="font-display text-3xl font-bold text-slate-900">Case Oversight</h1>
          <p className="mt-1 text-slate-500">Review visit requests, track timeliness, and generate quality reports.</p>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
          <Stat icon={ClipboardCheck} label="Pending" value={pending.length} tone="text-amber-600" bg="bg-amber-50" />
          <Stat icon={Check} label="Approved" value={stats.approved ?? 0} tone="text-emerald-600" bg="bg-emerald-50" />
          <Stat icon={TimerReset} label="Punctuality" value={`${stats.punctuality_index ?? 0}%`} tone="text-[#0d9488]" bg="bg-teal-50" />
          <Stat icon={TrendingUp} label="Completed" value={stats.completed ?? 0} tone="text-sky-600" bg="bg-sky-50" />
          <Stat icon={Users} label="Parents" value={parents.length} tone="text-[#1e3a8a]" bg="bg-blue-50" />
        </div>

        <Tabs defaultValue="queue" className="mt-8">
          <TabsList data-testid="sw-tabs">
            <TabsTrigger value="queue" data-testid="tab-queue">Approval Queue {pending.length > 0 && `(${pending.length})`}</TabsTrigger>
            <TabsTrigger value="cases" data-testid="tab-cases">My Cases</TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">All Visits</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports">AI Reports</TabsTrigger>
          </TabsList>

          {/* Queue */}
          <TabsContent value="queue" className="mt-4 space-y-3" data-testid="queue-panel">
            {pendingSeries.map((s) => (
              <div key={s.series_id} data-testid={`series-bar-${s.series_id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-indigo-200 bg-indigo-50/60 p-4">
                <div className="flex items-center gap-2 text-sm">
                  <CalendarClock className="h-4 w-4 text-indigo-600" />
                  <span className="font-semibold text-slate-800">{s.parent_name} → {s.child_name}</span>
                  <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium capitalize text-indigo-700">{s.recurrence} series · {s.count} pending</span>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" data-testid={`approve-series-${s.series_id}`} onClick={() => reviewSeries(s.series_id, "approved")} className="bg-emerald-600 hover:bg-emerald-700">
                    <Check className="mr-1.5 h-4 w-4" /> Approve all {s.count}
                  </Button>
                  <Button size="sm" variant="outline" data-testid={`decline-series-${s.series_id}`} onClick={() => reviewSeries(s.series_id, "not_approved")} className="border-red-200 text-red-600 hover:bg-red-50">
                    <X className="mr-1.5 h-4 w-4" /> Decline all
                  </Button>
                </div>
              </div>
            ))}
            {pending.length === 0 && (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
                <ClipboardCheck className="mx-auto h-10 w-10 text-slate-300" />
                <p className="mt-3 font-medium text-slate-600">No pending requests</p>
              </div>
            )}
            {pending.map((v) => (
              <div key={v.id} data-testid={`queue-card-${v.id}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-display text-lg font-semibold text-slate-900">{v.parent_name} → {v.child_name}</h3>
                    <div className="mt-1"><VisitTypeBadge type={v.visit_type} /></div>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-500">
                      <span className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4" />{v.visit_date}</span>
                      <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" />{v.start_time}–{v.end_time}</span>
                      <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{v.location}</span>
                    </div>
                    {v.notes && <p className="mt-2 text-sm italic text-slate-500">"{v.notes}"</p>}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" data-testid={`approve-btn-${v.id}`} onClick={() => review(v.id, "approved")} className="bg-emerald-600 hover:bg-emerald-700">
                      <Check className="mr-1.5 h-4 w-4" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`reject-btn-${v.id}`} onClick={() => setReviewTarget(v)} className="border-red-200 text-red-600 hover:bg-red-50">
                      <X className="mr-1.5 h-4 w-4" /> Decline
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </TabsContent>

          {/* My Cases (read-only — assignment controlled by admin) */}
          <TabsContent value="cases" className="mt-4" data-testid="cases-panel">
            <p className="mb-3 text-sm text-slate-500">These are the clients assigned to you by the administrator.</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {parents.map((p) => (
                <div key={p.id} data-testid={`case-card-${p.id}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1e3a8a] text-sm font-semibold text-white">
                      {p.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                    </div>
                    <div>
                      <p className="font-display text-base font-semibold text-slate-900">{p.name}</p>
                      <p className="text-xs text-slate-500">Child: {p.child_name || "N/A"}</p>
                      <span className="mt-1 inline-flex items-center gap-1 rounded-md bg-teal-50 px-2 py-0.5 text-[11px] font-medium text-[#0d9488]">
                        <UserCheck className="h-3 w-3" /> Your case
                      </span>
                    </div>
                  </div>
                </div>
              ))}
              {parents.length === 0 && <p className="py-8 text-center text-slate-400">No clients assigned to you yet.</p>}
            </div>
          </TabsContent>

          {/* All visits */}
          <TabsContent value="all" className="mt-4 space-y-3" data-testid="all-panel">
            {visits.map((v) => (
              <div key={v.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-800">{v.parent_name} → {v.child_name}</span>
                      <StatusBadge status={v.status} />
                      <VisitTypeBadge type={v.visit_type} />
                      {v.check_in_time && <TimelinessBadge value={v.timeliness} />}
                      {v.timeliness === "no_show" && <TimelinessBadge value="no_show" />}
                      {v.completed && <span className="rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-semibold text-sky-700">Completed</span>}
                      {v.assessment && <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2.5 py-0.5 text-xs font-semibold text-[#0d9488]"><ClipboardList className="h-3 w-3" /> Documented</span>}
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500">
                      <span>{v.visit_date} · {v.start_time}–{v.end_time}</span>
                      <span>{v.location}</span>
                    </div>
                    <RescheduleNote history={v.reschedule_history} />
                    {v.assessment && <QualitySummary assessment={v.assessment} />}
                  </div>
                  <div className="flex flex-wrap justify-end gap-2">
                    {!v.completed && v.timeliness !== "no_show" && (
                      <Button size="sm" variant="outline" data-testid={`reschedule-btn-${v.id}`} onClick={() => { setRescheduleTarget(v); setRescheduleOpen(true); }}>
                        <CalendarClock className="mr-1.5 h-4 w-4" /> Reschedule
                      </Button>
                    )}
                    {v.status === "approved" && !v.completed && !v.check_in_time && v.timeliness !== "no_show" && (
                      <Button size="sm" variant="outline" data-testid={`noshow-btn-${v.id}`} onClick={() => { setNoShowTarget(v); setNoShowReason(""); }} className="border-red-200 text-red-600 hover:bg-red-50">
                        <UserX className="mr-1.5 h-4 w-4" /> No Show
                      </Button>
                    )}
                    {(v.status === "approved" || v.completed) && (
                      <Button size="sm" variant="outline" data-testid={`document-btn-${v.id}`} onClick={() => openAssess(v)}>
                        <ClipboardList className="mr-1.5 h-4 w-4" /> {v.assessment ? "Edit Documentation" : "Document Quality"}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {visits.length === 0 && <p className="py-8 text-center text-slate-400">No visits recorded.</p>}
          </TabsContent>

          {/* Reports */}
          <TabsContent value="reports" className="mt-4" data-testid="reports-panel">
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-slate-900">
                  <Sparkles className="h-5 w-5 text-[#0d9488]" /> Generate Quality Report
                </h3>
                <p className="mt-1 text-sm text-slate-500">Select a parent to produce an AI-written quality-of-visit report.</p>
                <div className="mt-4 space-y-2" data-testid="parent-select-list">
                  {parents.map((p) => (
                    <div key={p.id} className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                      <div>
                        <p className="font-medium text-slate-800">{p.name}</p>
                        <p className="text-xs text-slate-500">
                          Child: {p.child_name || "N/A"}
                          {p.assigned_worker_name ? ` · Case: ${p.assigned_worker_name}` : " · Unassigned"}
                        </p>
                      </div>
                      <Button size="sm" data-testid={`generate-report-${p.id}`} disabled={genFor === p.id} onClick={() => generate(p.id)} className="bg-[#1e3a8a] hover:bg-[#172554]">
                        {genFor === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                        <span className="ml-1.5">Generate</span>
                      </Button>
                    </div>
                  ))}
                  {parents.length === 0 && <p className="text-sm text-slate-400">No parents registered yet.</p>}
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-slate-900">
                  <FileText className="h-5 w-5 text-[#1e3a8a]" /> Report History
                </h3>
                <div className="mt-4 space-y-2" data-testid="report-history">
                  {reports.map((r) => (
                    <button
                      key={r.id}
                      data-testid={`report-item-${r.id}`}
                      onClick={() => { setActiveReport(r); setReportOpen(true); }}
                      className="flex w-full items-center justify-between rounded-lg border border-slate-200 p-3 text-left transition-colors hover:border-[#0d9488] hover:bg-teal-50/40"
                    >
                      <div>
                        <p className="font-medium text-slate-800">{r.parent_name}</p>
                        <p className="text-xs text-slate-500">{new Date(r.created_at).toLocaleString()}</p>
                      </div>
                      <FileText className="h-4 w-4 text-slate-400" />
                    </button>
                  ))}
                  {reports.length === 0 && <p className="text-sm text-slate-400">No reports generated yet.</p>}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
      )}

      {/* Decline dialog */}
      <Dialog open={!!reviewTarget} onOpenChange={(o) => !o && setReviewTarget(null)}>
        <DialogContent className="sm:max-w-md" data-testid="decline-modal">
          <DialogHeader>
            <DialogTitle className="font-display">Decline Visit Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Reason (shared with parent)</Label>
            <Textarea data-testid="decline-reason-input" value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} rows={3} placeholder="Scheduling conflict, safety concern, etc." />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewTarget(null)}>Cancel</Button>
            <Button data-testid="confirm-decline-btn" onClick={() => review(reviewTarget.id, "not_approved", reviewNote)} className="bg-red-600 hover:bg-red-700">
              Confirm Decline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* No-show reason dialog */}
      <Dialog open={!!noShowTarget} onOpenChange={(o) => !o && setNoShowTarget(null)}>
        <DialogContent className="sm:max-w-md" data-testid="noshow-modal">
          <DialogHeader>
            <DialogTitle className="font-display">Mark as No Show</DialogTitle>
            <DialogDescription>
              {noShowTarget && `${noShowTarget.parent_name} → ${noShowTarget.child_name} · ${noShowTarget.visit_date}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Reason (included in the report)</Label>
            <Textarea data-testid="noshow-reason-input" value={noShowReason} onChange={(e) => setNoShowReason(e.target.value)} rows={3} placeholder="e.g., Parent did not arrive; no advance notice given." />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoShowTarget(null)}>Cancel</Button>
            <Button data-testid="confirm-noshow-btn" onClick={() => markNoShow(noShowTarget.id, noShowReason)} className="bg-red-600 hover:bg-red-700">
              Confirm No Show
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReportModal report={activeReport} open={reportOpen} onOpenChange={setReportOpen} />
      <AssessmentModal
        visit={assessTarget}
        open={assessOpen}
        onOpenChange={setAssessOpen}
        onSaved={load}
        onReschedule={(v) => { setRescheduleTarget(v); setRescheduleOpen(true); }}
      />
      <RescheduleModal open={rescheduleOpen} onOpenChange={setRescheduleOpen} visit={rescheduleTarget} onDone={load} />
    </div>
  );
}
